import { Controller } from '@hotwired/stimulus'
import Archiver, { ArchiveProgressReport } from '../../lib/archiver'
import BusyElementsController from './busy-elements'

export default class DownloadAllController extends Controller {
  declare readonly downloadableFileLinkTargets: HTMLAnchorElement[]
  declare readonly hasDownloadableFileLinkTarget: boolean
  declare readonly filenameTargets: HTMLElement[]
  declare readonly noticeTarget: HTMLButtonElement
  declare readonly triggerTarget: HTMLButtonElement
  declare readonly hasTriggerTarget: boolean
  declare readonly busyElementsOutlet: BusyElementsController
  declare readonly hasBusyElementsOutlet: boolean
  declare readonly progressReportTarget: HTMLElement
  declare readonly hasProgressReportTarget: boolean
  cancelArchive: () => void | null

  static targets = ['downloadableFileLink', 'notice', 'progressReport', 'trigger']

  static outlets = ['busy-elements']

  disconnect(): void {
    this.#handleCancelArchive()
  }

  execute() {
    this.#handleTriggerBusyState(true, {
      busyType: 'indeterminate',
      reportContent: 'Preparing Files',
    })

    if (!this.hasDownloadableFileLinkTarget) {
      console.warn('DownloadAllController: Nothing to download. Skipping.')
      this.#handleTriggerBusyState(false)
    } else {
      this.#handleBundleLinks()
    }
  }

  // Controller's Privates
  async #handleBundleLinks() {
    this.#handleTriggerBusyState(true, {
      busyType: 'indeterminate',
      reportContent: 'Downloading...',
    })

    const attachmentData = this.#extractFileDataFromLinks()
    const totalSize = attachmentData.reduce((total, current) => total + Number(current.at(2)), 0)
    if (this.downloadableFileLinkTargets.length === 1) {
      const fileUrl = this.downloadableFileLinkTargets.at(0).href
      // Force download
      window.open(fileUrl, '_blank')

      this.#handleTriggerBusyState(false)
    } else if (totalSize > 3 * 1024 ** 3) {
      // Total file size over 3GB
      if (
        !confirm(
          'Total file size is too large to bundle (>3GB). Your browser will download them separately. Make sure pop-ups are enabled for this site.',
        )
      ) {
        this.#handleTriggerBusyState(false)
        return
      }
      attachmentData.forEach(item => {
        window.open(item.at(1) as string, '_blank')
      })
      this.#handleTriggerBusyState(false)
    } else {
      const bundler = Archiver.init(
        attachmentData as Array<[string, string, number?]>,
        this.#handleProgressReport.bind(this),
      )
      this.cancelArchive = bundler.cancel.bind(bundler)
      bundler.bundleMe().finally(this.#handleTriggerBusyState.bind(this, false))
    }
  }

  #handleCancelArchive() {
    this.cancelArchive?.()
    this.cancelArchive = null
  }

  #handleProgressReport({ total, loaded, currentFileIndex, currentStep, totalFileCount }: ArchiveProgressReport) {
    if (!total) {
      return this.#handleTriggerBusyState(true, {
        busyType: 'indeterminate',
        reportContent: currentStep,
      })
    }
    const progress = total ? (loaded / total) * 100 : null
    this.#handleTriggerBusyState(true, {
      busyType: total ? 'progress' : 'indeterminate',
      progress,
      reportContent: `${currentStep}: ${currentFileIndex} of ${totalFileCount} (${progress.toFixed(1)}%)`,
    })
  }

  #handleTriggerBusyState(
    next: boolean,
    busyOptions?: {
      busyType: 'indeterminate' | 'progress'
      progress?: number
      reportContent?: string
    },
  ) {
    const { busyType, progress, reportContent } = busyOptions || {}

    this.triggerTarget.disabled = next
    if (!next) {
      this.triggerTarget.classList.remove('busy')
    } else if (!this.triggerTarget.classList.contains('busy')) {
      this.triggerTarget.classList.add('busy')
    }

    this.progressReportTarget.innerText = !next ? 'Download All Attachments' : reportContent
    if (this.hasBusyElementsOutlet) {
      this.busyElementsOutlet.setState(!next ? 'idle' : busyType, {
        progressTo: progress,
      })
    }
  }

  #extractFileDataFromLinks(): [string, string, number?, string?][] {
    return this.downloadableFileLinkTargets.map((linkEl, i) => {
      const url = linkEl.href
      const { downloadAllFilename: filename, downloadAllFilesize: filesize, downloadAllId: id } = linkEl.dataset
      return [filename || `File ${i + 1}`, url, Number(filesize), id]
    })
  }
}
